import { useDeleteWorkPlan, useGetAllWorkPlans } from "@api/workplanService";
import { useEffect, useState } from "react";
import { LinearProgress, Stack } from "@mui/joy";
import Box from '@mui/joy/Box';
import MenuItem from '@mui/joy/MenuItem';
import IconButton from '@mui/joy/IconButton';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import ObjectIndex from "@components/objects/ObjectIndex";
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import { formatDate } from "@utils/dates";
import { WorkPlanCloneModal } from "@components/WorkPlanCloneModal";

const columns = [
    {
        id: 'name',
        numeric: false,
        disablePadding: true,
        sortable: true,
        label: 'Name',
        cell: (row, {LinkComponent}) => {
            return (<>
                <LinkComponent to={`${row.id}`} relative={true} object={row}>
                    <Typography sx={{display: 'inline-block'}} fontWeight={'bold'}>{row.name}</Typography>
                </LinkComponent>

                {/* {row.owner && (<Typography fontSize="sm"><Typography fontWeight={'bold'}>Owner</Typography> <LinkComponent to={`/contacts/${row.owner.id}`} relative={true} object={row.owner}>{row.owner.name}</LinkComponent></Typography>)} */}

                {!!row.event_chair && (
                    <Typography fontSize="sm">
                        <Typography fontWeight={'bold'}>Event Chair</Typography>
                        {` `}
                        <LinkComponent to={`/contacts/${row.event_chair.id}`} object={row.event_chair}>
                            <Typography fontSize="sm">{row.event_chair.name}</Typography>
                        </LinkComponent>
                    </Typography>
                )}

                {!row.event_chair && row.contacts.length > 0 && (
                    <Typography fontSize="sm">
                        <Typography fontWeight={'bold'}>Primary Contact</Typography>
                        {` `}
                        <LinkComponent to={`/contacts/${row.contacts[0].id}`} object={row.contacts[0]}>
                            <Typography fontSize="sm">{row.contacts[0].name}</Typography>
                        </LinkComponent>
                    </Typography>
                )}
            </>)
        }
      },
      {
        id: 'tasks',
        numeric: false,
        disablePadding: false,
        sortable: false,
        label: 'Tasks',
        cell: (row) => {
            const progress = row.number_tasks ? (((row.number_tasks - row.number_incomplete_tasks) / row.number_tasks) * 100) : 0;
            return (
                <Stack
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="center"
                  spacing={2}
                >
                <Box sx={{flex: '0 1 25%', display: 'flex', justifyContent: 'flex-end'}}>
                        <LinearProgress determinate value={progress} sx={{backgroundColor: 'var(--joy-palette-background-backdrop)'}} />
                    </Box>
                    <Box sx={{flex: 'auto', whiteSpace: 'nowrap'}}>
                        <Tooltip title={'Completed Tasks'}>
                            <Box sx={{display: 'inline'}}>{row.number_tasks - row.number_incomplete_tasks}</Box>
                        </Tooltip>
                        {' of '}
                        <Tooltip title={'Total Tasks'}>
                            <Box sx={{display: 'inline'}}>{row.number_tasks}</Box>
                        </Tooltip>
                    </Box>

                </Stack>
            )
        }
      },
      {
        id: 'workplan_date',
        numeric: false,
        disablePadding: false,
        sortable: true,
        label: 'Date',
        cell: (row) => formatDate(row.workplan_date),
      },
];

const listCell = (row, {LinkComponent}) => {
    return (<>
        <Box sx={{mb: 1}}>
            <Typography sx={{display: 'inline-block'}} fontWeight={'bold'}>
                <LinkComponent to={`${row.id}`} relative={true} object={row}>
                    {row.name}
                </LinkComponent>
            </Typography>
            {row.contacts.length > 0 && (
                <LinkComponent to={`/contacts/${row.contacts[0].id}`} object={row.contacts[0]}>
                    <Typography fontSize="sm">{row.contacts[0].name}</Typography>
                </LinkComponent>
            )}
        </Box>
    </>);
};

/*
const listActions = (row, {view, LinkComponent, ViewAction}) => {
    if(view === 'deleted') {
        return null;
    }

    return (<>
        <IconButton
            size="sm"
            color="neutral"
            variant="outlined"
            sx={{ bgcolor: 'background.surface', ":disabled": {opacity: 0.3} }}
            disabled={!row.email}
            component={'a'}
            href={`mailto:${row.email}`}
            >
            <EmailRoundedIcon />
        </IconButton>
        <IconButton
            size="sm"
            color="neutral"
            variant="outlined"
            sx={{ bgcolor: 'background.surface', ":disabled": {opacity: 0.3} }}
            disabled={!getPreferredPhone(row)}
            component={'a'}
            href={`tel:${getPreferredPhone(row)}`}
            >
            <LocalPhoneRoundedIcon />
        </IconButton>
    </>);
};
*/

const WorkPlanCloneButton = ({data, open, setOpen, ...restProps}) => {
    return (<>
        <Tooltip title="Clone a Work Plan">
            <IconButton onClick={() => setOpen(true)} sx={{ bgcolor: 'background.surface' }} {...restProps} data-first-child>
                <ContentCopyRoundedIcon/>
            </IconButton>
        </Tooltip>
    </>)
};

const WorkPlanCloneMenuItem = ({row, open, setOpen, ...restProps}) => {
    return (<>
        <MenuItem onClick={() => setOpen(true, row)}>Clone</MenuItem>
    </>)
};

export default function WorkPlanIndex() {
    const [cloningOpen, _unstable_setCloningOpen] = useState(false);
    const [cloningWorkplan, setCloningWorkplan] = useState(null);
    const setCloningOpen = (state, workplan = null) => {
        setCloningWorkplan(workplan);
        _unstable_setCloningOpen(state);
    }

    return (<>
        <ObjectIndex
            query={useGetAllWorkPlans}
            deleteMutation={useDeleteWorkPlan}
            type={'workplan'}
            permissionType={'work plans'}
            title={'Work Plans'}
            color={'workplan'}
            columns={columns}
            listCell={listCell}
            // listActions={listActions}
            additionalHeaderActions={(({data, ...restProps}) => (<><WorkPlanCloneButton data={data} open={cloningOpen} setOpen={setCloningOpen} {...restProps} /></>))}
            additionalMoreActions={(({row, ...restProps}) => (<><WorkPlanCloneMenuItem row={row} open={cloningOpen} setOpen={setCloningOpen} {...restProps} /></>))}
        />
        <WorkPlanCloneModal workplan={cloningWorkplan} open={cloningOpen} setOpen={setCloningOpen} />
    </>)
}
